import { defineStore } from 'pinia'

// Import lazy del router per spezzare il ciclo di dipendenze:
//   stores/list → @/router → (componenti/store importati eager) → mixins/list → @/stores/list
// L'import statico di @/router faceva valutare mixins/list.js mentre `useListStore` era
// ancora in TDZ → "Cannot access 'useListStore' before initialization".
// Stesso pattern già usato in services/http.js. Le action usano il router solo a runtime
// (interazione utente), quando il modulo router è già caricato: l'import dinamico risolve
// istantaneamente dalla cache ESM. La navigazione resta fire-and-forget (action sincrona).
let _routerPromise = null
const withRouter = (fn) => {
  if (!_routerPromise) _routerPromise = import('@/router').then((m) => m.default)
  _routerPromise.then(fn)
}

// Port modulo Vuex `list` → Pinia.
// - getters parametrizzati (state => key => ...) restano funzioni: in Pinia un getter
//   può restituire una funzione, quindi l'uso listRowsByKey(key) resta identico.
// - router.app.$route.query (Vue Router 3) → router.currentRoute.value.query (Vue Router 4).
// - mutations fuse nelle actions.

const listState = {
  key: '',
  rows: [],
  sorting: { prop: null, order: null },
  query: '',
  filters: {},
  ready: false,
}

const LIST_KEYS = [
  'userCustomers', 'customerDocuments', 'customerContacts', 'customerOrders',
  'customerOffers', 'customerBills', 'customerExpirations', 'CustomerConsignmentPayment',
  'customerReceipts', 'customerSales', 'customerRelations', 'userProducts',
  'productAvailabilities', 'productReplacer', 'productCrossReferences', 'productArrivals',
  'productHistory', 'productCommitments', 'userOffers', 'userSuggestedProducts',
  'insertedDocuments', 'cartElements', 'offerItems', 'recommendedActivities',
  'openedTasks', 'sharedTasks', 'tagsManagement', 'dailyDocuments', 'backorderDocuments',
  'backorderDocumentsRows', 'retiredDocuments', 'offersSolicit', 'unsolvedDocuments',
  'expiredDocuments', 'customersRisk', 'customersNew', 'pendingDocuments',
  'documentAnalysis', 'customerGoods', 'customerTicketsCashed', 'customerDestinations',
  'salesProductsTab', 'lastProductsTab', 'receiptsNotReceived', 'customerBonus',
  'documentMargins', 'customerDiscounts',
]

const makeInitialState = () =>
  LIST_KEYS.reduce((acc, key) => ({ ...acc, [key]: { ...listState } }), {})

const normalizeSorting = ({ prop, order }) => {
  if (!prop || !order) return null
  const direction = order === 'ascending' ? 'ASC' : 'DESC'
  return `${prop}:${direction},createdAt:DESC`
}

const getListKey = (listKey) => listKey || LIST_KEYS[0]

export const useListStore = defineStore('list', {
  state: () => makeInitialState(),

  getters: {
    listRowsByKey: (state) => (listKey) => state[getListKey(listKey)].rows,
    listSortingByKey: (state) => (listKey) => state[getListKey(listKey)].sorting,
    listQueryByKey: (state) => (listKey) => state[getListKey(listKey)].query,
    listFiltersByKey: (state) => (listKey) => state[getListKey(listKey)].filters,
    listParamsByKey: (state) => (listKey) => {
      const order = normalizeSorting(state[getListKey(listKey)].sorting)
      return {
        order: order || undefined,
        query: state[getListKey(listKey)].query || undefined,
        ...state[getListKey(listKey)].filters,
      }
    },
    listByKeyIsReady: (state) => (listKey) => state[getListKey(listKey)].ready,
  },

  actions: {
    setListRows({ listKey, rows }) {
      this[listKey].rows = rows
    },
    setListSorting({ listKey, sorting }) {
      this[listKey].sorting = sorting
      withRouter((router) => {
        router.push({
          query: { ...router.currentRoute.value.query, order: normalizeSorting(sorting) },
        })
      })
    },
    setListQuery({ listKey, query }) {
      this[listKey].query = query
    },
    setListFilter({ listKey, filter }) {
      this[listKey].filters = { ...this[listKey].filters, ...filter }
      withRouter((router) => {
        router
          .replace({ query: { ...router.currentRoute.value.query, ...filter } })
          .catch(() => {})
      })
    },
    setListIsReady({ listKey, ready }) {
      this[listKey].ready = ready
    },
    setListFilters({ listKey, filters }) {
      this[listKey].filters = filters
      withRouter((router) => {
        const current = router.currentRoute.value.query
        const query = Object.keys(current).reduce(
          (acc, key) => (!key.includes('filter') ? { ...acc, [key]: current[key] } : acc),
          {},
        )
        router.replace({ query: { ...query, ...filters } }).catch(() => {})
      })
    },
    resetList(listKey) {
      Object.keys(listState).forEach((key) => {
        this[listKey][key] = listState[key]
      })
    },
    resetLists(listKeys) {
      listKeys.forEach((listKey) => {
        Object.keys(listState).forEach((key) => {
          this[listKey][key] = listState[key]
        })
      })
    },
  },
})
